import { useCallback, useEffect, useRef, useState } from 'react';

const MIN = 1;
const MAX = 5;
const STAP = 1.4;
const RUST = { s: 1, x: 0, y: 0 };
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const afstand = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// Pinch, dubbeltik, wiel en slepen voor de fotoviewer. onSwipe(-1|1) bij een veeg op 100%.
export const useZoomPan = (resetKey, onSwipe) => {
  const ref = useRef(null);
  // callback-ref: de Radix-portal mount later dan deze component rendert
  const [podium, setPodium] = useState(null);
  const zetRef = useCallback((node) => { ref.current = node; setPodium(node); }, []);
  const [t, setT] = useState(RUST);
  const [bezig, setBezig] = useState(false);
  const tRef = useRef(RUST);
  const pointers = useRef(new Map());
  const gebaar = useRef(null);
  const laatsteTik = useRef(0);
  const swipe = useRef(onSwipe);
  swipe.current = onSwipe;

  const zet = useCallback((n) => {
    const el = ref.current;
    const s = clamp(n.s, MIN, MAX);
    const maxX = el ? ((s - 1) * el.clientWidth) / 2 : 0;
    const maxY = el ? ((s - 1) * el.clientHeight) / 2 : 0;
    const volgende = s === 1 ? RUST : { s, x: clamp(n.x, -maxX, maxX), y: clamp(n.y, -maxY, maxY) };
    tRef.current = volgende;
    setT(volgende);
  }, []);

  useEffect(() => { pointers.current.clear(); gebaar.current = null; zet(RUST); }, [resetKey, zet]);

  const zoomNaar = useCallback((s) => zet({ ...tRef.current, s }), [zet]);

  useEffect(() => {
    if (!podium) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      zoomNaar(tRef.current.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
    };
    podium.addEventListener('wheel', onWheel, { passive: false });
    return () => podium.removeEventListener('wheel', onWheel);
  }, [podium, zoomNaar]);

  const onPointerDown = (e) => {
    if (e.target.closest('button')) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const lijst = [...pointers.current.values()];
    setBezig(true);
    if (lijst.length === 2) {
      gebaar.current = { soort: 'pinch', d0: afstand(lijst[0], lijst[1]), s0: tRef.current.s, moved: true };
    } else if (lijst.length === 1) {
      gebaar.current = { soort: 'sleep', x0: e.clientX, y0: e.clientY, t0: tRef.current, moved: false };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gebaar.current;
    if (!g) return;
    if (g.soort === 'pinch') {
      const [a, b] = [...pointers.current.values()];
      if (a && b && g.d0 > 0) zoomNaar(g.s0 * (afstand(a, b) / g.d0));
      return;
    }
    const dx = e.clientX - g.x0;
    const dy = e.clientY - g.y0;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) g.moved = true;
    g.dx = dx;
    g.dy = dy;
    if (g.t0.s > 1) zet({ s: g.t0.s, x: g.t0.x + dx, y: g.t0.y + dy });
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    const g = gebaar.current;
    const rest = [...pointers.current.values()];
    if (g?.soort === 'pinch') {
      gebaar.current = rest.length === 1
        ? { soort: 'sleep', x0: rest[0].x, y0: rest[0].y, t0: tRef.current, moved: true } : null;
      if (tRef.current.s < 1.05) zet(RUST);
    } else if (g?.soort === 'sleep') {
      gebaar.current = null;
      if (!g.moved) {
        const nu = Date.now();
        if (nu - laatsteTik.current < 300) {
          laatsteTik.current = 0;
          zet(tRef.current.s > 1 ? RUST : { s: 2.5, x: 0, y: 0 });
        } else {
          laatsteTik.current = nu;
        }
      } else if (g.t0.s === 1 && Math.abs(g.dx || 0) > 50 && Math.abs(g.dx) > Math.abs(g.dy || 0)) {
        swipe.current?.(g.dx < 0 ? 1 : -1);
      }
    }
    if (rest.length === 0) setBezig(false);
  };

  return {
    ref: zetRef, t, bezig,
    in: () => zoomNaar(tRef.current.s * STAP),
    uit: () => zoomNaar(tRef.current.s / STAP),
    reset: () => zet(RUST),
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  };
};
